/**
 * Launcher V2 — the client-side parser for `generate-copy`'s streaming
 * response (U4b of
 * plans/2026-09-10-001-launcher-v2-ai-copy-editing-and-usage-sidebar-plan.md,
 * §U4). The server half is `openEventStream` with `jsonData` (`_shared/event-stream.ts`);
 * this file stays a plain isomorphic module so `api-client.ts` (a frontend import)
 * can pull `parseSseChunk` without a server-only dependency.
 *
 * Frame shape: `data: <json>\n\n`. `parseSseChunk` is pure (no I/O) so it
 * unit-tests without a server or a real stream.
 */

/**
 * Reassembles SSE frames out of a `buffer` (leftover from the previous call)
 * plus the newly arrived `chunk`. Frames are separated by a blank line
 * (`\n\n`); a frame that hasn't seen its closing `\n\n` yet — because it was
 * split across two chunks — is returned as `rest` for the next call.
 *
 * A frame that doesn't start with `data: `, or whose JSON fails to parse, is
 * skipped rather than thrown — a single malformed/incomplete frame must never
 * take down the whole stream (Codex-challenge resolution, U4b).
 */
export function parseSseChunk(buffer: string, chunk: string): { events: unknown[]; rest: string } {
  const combined = buffer + chunk;
  const parts = combined.split('\n\n');
  const rest = parts.pop() ?? '';

  const events: unknown[] = [];
  for (const part of parts) {
    const trimmed = part.trim();
    if (trimmed === '' || !trimmed.startsWith('data: ')) continue;
    const jsonText = trimmed.slice('data: '.length);
    try {
      events.push(JSON.parse(jsonText));
    } catch {
      // Malformed frame — skipped, never thrown.
    }
  }
  return { events, rest };
}
